// ✅ All imports go at the top
import React, { useRef } from "react";
import {
  Avatar,
  Col,
  Row,
  Tag,
  Carousel,
  Image,
  Typography,
  Spin,
  Flex,
} from "antd";
import { useParams } from "react-router-dom";
import "./home.css";
import { ReactComponent as Duration } from "../assets/images/duration.svg";
import { ReactComponent as Transfer } from "../assets/images/transport-icon.svg";
import { ReactComponent as Ticket } from "../assets/images/ticket.svg";
import verifyIcon from "../assets/images/verify.svg";
import BallIcon from "../assets/images/ball-svgrepo.svg";

// Components
import TravelBanner from "../components/TravelBanner";
import ItineraryViewer from "../components/ItineraryData";
import BookingSummary from "../components/BookingSummary";
import TourInclusionsTabs from "../components/TourInclusionsTabs";
import ReviewSlider from "../components/ReviewSlider";
// Hooks
import useProductDetailData from "../hooks/useProductDetailData";
import SpatialOffers from "../components/SpatialOffers";
import FAQAccordion from "../components/FAQAccordion";
import ActivitiesBlock from "../components/ActivitiesBlock";
import YourItinerary from "../components/YourItinerary";
import FlightDetailsTable from "../components/FlightDetailsTable";
import RelatedProducts from "../components/RelatedProducts";

// Typography shortcut
const { Title, Text, Paragraph } = Typography;

// ✅ Component
const ProductDetailsPage = () => {
  const { detailSlug } = useParams();
  const carouselRef = useRef(null);

  const { detail, loading, error } = useProductDetailData(detailSlug);

  const renderChildren = (children = []) => {
    return children.map((child, index) => {
      // LINK
      if (child.type === "link") {
        return (
          <a
            key={index}
            href={child.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:opacity-80 no-underline"
          >
            {child.children?.map((c) => c.text).join("")}
          </a>
        );
      }

      // TEXT
      return child.text || "";
    });
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen bg-white/70">
        <Spin size="large" tip="Loading product details..." />
      </div>
    );

  if (error) return <p className="text-center text-red-500">{error}</p>;
  if (!detail) return <p className="text-center">No detail found.</p>;

  const itinerary = detail.itinerary;

  const bannerImages =
    detail?.images?.length > 0
      ? detail.images.map((img) => `${process.env.REACT_APP_API_URL}${img.url}`)
      : ["/default-banner.jpg"];

  const tourOverview =
    detail?.Tour_overview && detail.Tour_overview.length > 0
      ? detail.Tour_overview[0]
      : null;
  const itineraryStops = tourOverview?.itinerary_stop || [];
  const reviewSection = detail?.review_section?.[0] || {};
  const rating = detail?.rating;

  const dayActivities = detail?.itinerary?.Days?.[0]?.activity || [];

  // --- ACTIVITY DATA ---

  const activities = detail?.itinerary?.Days?.[0]?.activity || [];



  // --- MAP TO ActivitiesBlock FORMAT ---
  const mappedActivities = activities.map((act) => ({
    image: act?.activity_image?.url
      ? `${process.env.REACT_APP_API_URL}${act.activity_image.url}`
      : null,

    name: act?.activity_name || "ACTIVITY",
    location: act?.location,
    description: act?.description?.[0]?.children?.[0]?.text || "",

    duration: act?.duration ? { value: act.duration, available: true } : null,

    lunch:
      act?.lunch_included !== null
        ? {
          value: "Lunch",
          available: act.lunch_included === true,
        }
        : null,

    dinner:
      act?.dinner_included !== null
        ? {
          value: "Dinner",
          available: act.dinner_included === true,
        }
        : null,
  }));

  // --- Your Itinerary DATA ---

  const yourItinerary = detail?.itinerary?.Days?.[0]?.your_itinerary || [];

  // --- MAP TO ActivitiesBlock FORMAT ---
  const mappedYourItinerary = yourItinerary.map((act) => ({
    image: act?.activity_image?.url
      ? `${process.env.REACT_APP_API_URL}${act.activity_image.url}`
      : null,

    name: act?.activity_name || "ACTIVITY",
    location: act?.location,
    description: act?.description?.[0]?.children?.[0]?.text || "",

    duration: act?.duration ? { value: act.duration, available: true } : null,

    lunch:
      act?.lunch_included !== null
        ? {
          value: "Lunch",
          available: act.lunch_included === true,
        }
        : null,

    dinner:
      act?.dinner_included !== null
        ? {
          value: "Dinner",
          available: act.dinner_included === true,
        }
        : null,
  }));

  const carouselSettings = {
    dots: false,
    infinite: true,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: false,
    responsive: [
      {
        breakpoint: 1024,
        settings: { slidesToShow: 2 },
      },
      {
        breakpoint: 640,
        settings: { slidesToShow: 1 },
      },
    ],
  };

  // --- FACILITIES DATA ---
  const facilities = detail?.itinerary?.Days?.[0]?.Facilities || [];

  // --- MAP TO ActivitiesBlock FORMAT ---
  const mappedFacilities = facilities.map((act) => ({
    image: act?.activity_image?.url
      ? `${process.env.REACT_APP_API_URL}${act.activity_image.url}`
      : null,

    name: act?.activity_name || "FACILITY",
    location: act?.location,

    description: act?.description?.[0]?.children?.[0]?.text || "",

    duration: act?.duration ? { value: act.duration, available: true } : null,

    lunch:
      act?.lunch_included !== null
        ? {
          value: "Lunch",
          available: act.lunch_included === true,
        }
        : null,

    dinner:
      act?.dinner_included !== null
        ? {
          value: "Dinner",
          available: act.dinner_included === true,
        }
        : null,
  }));


  const renderRichText = (blocks) => {
    if (!blocks?.length) return null;

    return blocks.map((block, index) => {
      if (block.type === "paragraph") {
        return (
          <Paragraph key={index} className="font-normal pb-0">
            {block.children?.map((child, childIndex) => (
              <span key={childIndex}>{child.text}</span>
            ))}
          </Paragraph>
        );
      }

      if (block.type === "list") {
        return (
          <ul key={index} className="list-disc pl-5">
            {block.children?.map((item, itemIndex) => (
              <li key={itemIndex}>
                {item.children?.map((child, childIndex) => (
                  <span key={childIndex}>{child.text}</span>
                ))}
              </li>
            ))}
          </ul>
        );
      }

      return null;
    });
  };

  return (
    <>
      <section id="banner-section" className=" text-white mx-6 md:mx-0">
        <div className="container">
          <TravelBanner
            title={detail.title?.trim()}
            images={bannerImages}
            price={detail.final_price}
            oldPrice={detail.old_price}
            savePrice={detail.save_price}
            rating={detail.rating}
            views={detail.views}
            shortDescription={detail.short_description}
          />
        </div>
      </section>

      <div className="container">
        <Row gutter={{ xs: 0, md: 30 }}>
          {/* LEFT FULL PAGE CONTENT */}
          <Col xs={24} sm={18}>
            <div className="grid grid-cols-2 md:grid-cols-[2fr_1fr_1fr] mb-5 mt-8 mx-6 sm:px-0">
              <Title className="col-span-2 md:col-span-1 pb-4 px-0 !mb-0 pb-0 !text-primary !font-semibold !text-[20px] md:!text-[28px]">
                {" "}
                {detail.title}{" "}
              </Title>

              {/* ROW 1 */}
              {rating && (
                <div className="grid grid-cols-1 md:mb-5">
                  <div className="flex items-center justify-start md:justify-center gap-1 text-center leading-tight">
                    <div className="text-2xl text-primary">★</div>
                    <div className="text-sm text-danger font-semibold">
                      {rating}
                    </div>
                  </div>
                </div>
              )}

              {detail.tour_type && (
                <Flex
                  align="center"
                  className="mt-2 md:mb-4 text-muted text-sm font-medium md:justify-center"
                >
                  Tour Type
                  <Flex align="center">
                    <Tag className="px-3 py-1 border border-[#0389FF] bg-blue-100 text-[#3F6385] font-medium rounded text-xs ml-2">
                      {detail.tour_type}
                    </Tag>
                    <img
                      className="!ml-[-20px] relative"
                      preview={false}
                      src={verifyIcon}
                    />
                  </Flex>
                </Flex>
              )}
            </div>

            <section className="itinerary-section pb-6 mx-6 sm:px-0">

              <div className="w-full rounded-lg text-primary text-sm">

                <div className="grid grid-cols-2 md:grid-cols-4">
                  {/* ROW 2 */}
                  {detail.ticket_price && (
                    <>

                      <div className="flex items-center">
                        <Avatar className="!w-[30px] !h-[30px]  lg:!w-[50px] lg:!h-[50px] p-1 lg:p-3"
                          size={50}
                          icon={<Duration style={{ fill: "#5BA268" }} />}
                          style={{
                            backgroundColor: "#E1FFE6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                          }}
                        />
                        <div className="p-2 md:p-4 flex flex-col">
                          <span className="font-medium text-[#000] text-xs md:text-sm">
                            Duration:
                          </span>{" "}
                          <span className="text-[#898989] font-normal text-xs md:text-sm">
                            {" "}
                            {detail.duration}{" "}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <Avatar
                          className="!w-[30px] !h-[30px]  lg:!w-[50px] lg:!h-[50px] p-1 lg:p-3"
                          size={50}
                          icon={<Transfer style={{ fill: "#A68958" }} />}
                          style={{
                            backgroundColor: "#FFE5B9",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        />
                        <div className="p-2 md:p-4 flex flex-col">
                          <span className="font-medium text-[#000] text-xs md:text-sm">
                            Transfer Type:
                          </span>{" "}
                          <span className="text-[#898989] font-normal text-xs md:text-sm">
                            {" "}
                            {detail.transfer_type}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <Avatar
                          className="!w-[30px] !h-[30px]  lg:!w-[50px] lg:!h-[50px] p-1 lg:p-3"
                          size={50}
                          icon={<Ticket style={{ fill: "#407FA0" }} />}
                          style={{
                            backgroundColor: "#E8F2FF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                          }}
                        />
                        <div className="p-2 md:p-4 flex flex-col">
                          <span className="font-medium text-[#000] text-xs md:text-sm">
                            Ticket Price:
                          </span>{" "}
                          <span className="text-[#898989] font-normal text-xs md:text-sm">
                            {" "}
                            {detail.ticket_price}
                          </span>
                        </div>
                      </div>

                      {/* PACKAGE DETAILS */}
                      {!tourOverview &&
                        detail?.package_details?.map(
                          (item) =>
                            item.value && (
                              <div key={item.id}>
                                <div className="flex items-center">
                                  <Avatar className="!w-[30px] !h-[30px]  lg:!w-[50px] lg:!h-[50px] p-1 lg:p-3"
                                    size={50}
                                    src={
                                      item.icon?.url
                                        ? `${process.env.REACT_APP_API_URL}${item.icon.url}`
                                        : undefined
                                    }
                                    icon={
                                      !item.icon?.url ? (
                                        <Ticket style={{ fill: "#407FA0" }} />
                                      ) : undefined
                                    }
                                    style={{
                                      backgroundColor: item.backgroundColor
                                        ? `#${item.backgroundColor.replace("#", "")}`
                                        : "#E8F2FF",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",

                                    }}
                                  />

                                  <div className="p-2 md:p-4 flex flex-col">
                                    <span className="font-medium text-[#000] text-xs md:text-sm">
                                      {item.label}:
                                    </span>

                                    <span className="text-[#898989] font-normal text-xs md:text-sm">
                                      {item.value}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ),
                        )}
                    </>
                  )}
                </div>
              </div>

 

              {tourOverview && (
                <Flex className="mt-8 flex-col md:flex-row" align="center" gap={15}>
                  <Tag className="px-6 py-2 bg-danger text-white font-medium rounded text-sm ml-2">
                    {tourOverview.total_days} days & {tourOverview.total_nights}{" "}
                    nights
                  </Tag>

                  <div className="flex items-center justify-center">
                    {itineraryStops.map((stop, index) => (
                      <div key={stop.id} className="flex items-center">
                        <div className="flex flex-col items-center">
                          <Flex className="flex-col md:flex-row" align="center" gap={2}>
                            <Text className="text-[#C4BFBF] font-extrabold text-5xl leading-[48px]">
                              {stop.days}
                            </Text>
                            <Flex className="flex-col md:flex-row items-center gap-1">
                              <Text className="!text-xs leading-none">
                                Days
                              </Text>
                              <Text className="!text-sm !font-medium text-danger leading-none">
                                {stop.city_name}
                              </Text>
                            </Flex>
                          </Flex>
                        </div>

                        {index < itineraryStops.length - 1 && (
                          <span className="mx-3 text-gray-400">→</span>
                        )}
                      </div>
                    ))}
                  </div>
                </Flex>
              )}

              {/* Other sections (FAQ, Gallery, Reviews, etc.) */}
              {/* Example: FAQ */}
              {detail.faq.length > 0 && (
                <div className="mt-10">
                  <h2 className="text-xl font-semibold mb-4">FAQ</h2>
                  {detail.faq.map((q) => (
                    <div key={q.id} className="mb-2">
                      <p className="font-medium">
                        {q.question || "No question"}
                      </p>
                      <p>{q.answer || "No answer"}</p>
                    </div>
                  ))}
                </div>
              )}

              {detail.product_detail_summary?.map((block, idx) => {
                // HEADING
                if (block.type === "heading") {
                  const Tag = `h${block.level || 3}`;
                  return (
                    <Tag key={idx} className="font-semibold mt-3 mb-1">
                      {block.children?.map((c) => c.text).join(" ")}
                    </Tag>
                  );
                }

                // PARAGRAPH (default)
                return (
                  <p key={idx} className="font-medium mt-5">
                    {block.children?.map((c) => c.text).join(" ")}
                  </p>
                );
              })}

              {/* <Divider /> */}
              <Title
                className="!font-medium !text-danger !text-xl md:!text-2xl !mb-4 mt-5"
                level={2}
              >
                <span className="text-primary">Trip </span> Highlights
              </Title>

              {detail.description?.map((block, idx) => {
                // LIST
                if (block.type === "list") {
                  return (
                    <ul key={idx} className="list-disc pl-6 space-y-2">
                      {block.children?.map((item, i) => (
                        <li key={i} className="font-medium">
                          {renderChildren(item.children)}
                        </li>
                      ))}
                    </ul>
                  );
                }

                // HEADING
                if (block.type === "heading") {
                  const Tag = `h${block.level || 3}`;

                  return (
                    <Tag key={idx} className="font-semibold mt-3 mb-1">
                      {renderChildren(block.children)}
                    </Tag>
                  );
                }

                // PARAGRAPH
                return (
                  <p key={idx} className="font-medium">
                    {renderChildren(block.children)}
                  </p>
                );
              })}
 

              {itinerary?.Days?.length > 0 ? (
                <ItineraryViewer itineraryData={itinerary} />
              ) : (
                <p className="text-center text-gray-600"></p>
              )}

              {/* FLIGHT DETAILS */}
              {detail.flight_details?.length > 0 && (
                <FlightDetailsTable flightDetails={detail.flight_details} />
              )}
              {/* <ItineraryData itineraryData={detail.itineraryData} /> */}
            </section>
            {mappedActivities.length > 0 && (
              <section className="itinerary-section py-4 px-6 md:px-4 sm:px-0">
                {/* HEADER */}
                <Title
                  level={5}
                  className="flex !text-sm !mb-4 gap-2 items-center"
                >
                  <img src={BallIcon} alt="activity" />
                  Activities
                </Title>

                {mappedActivities.length > 0 && (
                  <section className="mt-4">
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => carouselRef.current?.prev()}
                        className="absolute left-0 top-1/2 z-10 -translate-y-1/2 
               flex h-10 w-10 items-center justify-center 
               rounded-full bg-white shadow-md"
                      >
                        <span className="text-2xl">‹</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => carouselRef.current?.next()}
                        className="absolute right-0 top-1/2 z-10 -translate-y-1/2 
               flex h-10 w-10 items-center justify-center 
               rounded-full bg-white shadow-md"
                      >
                        <span className="text-2xl">›</span>
                      </button>

                      <Carousel ref={carouselRef} {...carouselSettings}>
                        {mappedActivities.map((activity, index) => (
                          <div key={index} className="px-2">
                            <ActivitiesBlock
                              activity={activity}
                              title="Activities"
                            />
                          </div>
                        ))}
                      </Carousel>
                    </div>
                  </section>
                )}
              </section>
            )}

            <section className="py-4 mx-6 sm:px-0">
              {detail?.sections?.length ? (
                <TourInclusionsTabs sections={detail.sections} />
              ) : (
                <p className="text-center text-gray-500">
                  No sections available.
                </p>
              )}
            </section>

            {mappedYourItinerary.length > 0 && (
              <section className="itinerary-section  py-4 mx-6 sm:px-0">
                {/* HEADER */}
                <Title
                  level={5}
                  className="flex !text-sm !mb-4 gap-2 items-center"
                >
                  <img src={BallIcon} alt="activity" />
                  Your Itinerary
                </Title>

                {mappedYourItinerary.length > 0 && (
                  <section className="">
                    <Carousel {...carouselSettings}>
                      {mappedYourItinerary.map((activity, index) => (
                        <div key={index} className="px-2">
                          <YourItinerary
                            activity={activity}
                            title="Activities"
                          />
                        </div>
                      ))}
                    </Carousel>
                  </section>
                )}
              </section>
            )}

            {mappedFacilities.length > 0 && (
              <section className="itinerary-section py-4 mx-6 sm:px-0">
                {/* HEADER */}
                <Title
                  level={5}
                  className="flex !text-sm !mb-4 gap-2 items-center"
                >
                  <img src={BallIcon} alt="facility" />
                  Facilities
                </Title>

                <section className="">
                  <Carousel {...carouselSettings}>
                    {mappedFacilities.map((activity, index) => (
                      <div key={index} className="px-2">
                        <YourItinerary activity={activity} title="Facilities" />
                      </div>
                    ))}
                  </Carousel>
                </section>
              </section>
            )}
            {/* <section className="py-6">
        <TourInclusionsTabs />
      </section> */}

            {detail?.FAQ?.length > 0 && (
              <section className="py-4 mx-6 sm:px-0">
                <FAQAccordion faqList={detail?.FAQ} />
              </section>
            )}

            {detail?.review_section?.length > 0 && (
              <section className="py-4 mx-6 lg:px-0">
                <ReviewSlider
                  reviews={reviewSection.reviews || []}
                  ratingLevels={reviewSection.rating_levels || []}
                  totalRatings={reviewSection.total_reviews || 0}
                  averageRating={reviewSection.average_rating || 0}
                  totalCountries={detail?.totalCountries || 65} // optional
                />
              </section>
            )}

            {/* RELATED PRODUCTS */}
            <section className="py-4 mx-6 lg:px-0">
              {detail.related_products?.length > 0 && (
                <RelatedProducts products={detail.related_products} />
              )}
            </section>

            {/* Offers Section */}
            <section className="py-4 mx-6 lg:px-0">
              <div className="flex justify-center items-center mb-6 flex-col">
                <Title
                  className="!font-medium !text-danger !text-2xl !mb-1 text-center"
                  level={2}
                >
                  <span className="text-primary">Offer</span> {"for you"}
                </Title>
                <Paragraph className="!mb-0">
                  Culture, nature, thrills, and record-breaking experiences
                </Paragraph>
              </div>

              <SpatialOffers />
            </section>
          </Col>
          {/* RIGHT SIDEBAR */}
          <Col xs={24} sm={6}>
            <div className="sidebar-sticky mt-4 z-[10] mx-6 sm:mx-0 gap-4">
              <BookingSummary
                booking={detail.booking_info}
                productDetailId={detail.id}
              />

              <div className="rounded-xl border border-[#CCE1F9] h-fit mt-4">
                <h3 className="px-6 py-4 !text-[15px] font-normal text-primary border-b border-[#CCE1F9]">
                  Why Choose Efficient Tourism ?
                </h3>
                <ul className="px-6 py-4">
                  {detail?.why_choose_item?.map((item) => (
                    <li key={item.id}>
                      <Flex
                        gap={10}
                        className="grid grid-cols-[30px_1fr] text-primary font-normal text-lg mb-2"
                      >
                        <Image
                          src={`${process.env.REACT_APP_API_URL}${item.icon?.url}`}
                          preview={false}
                          width={30}
                        />
                        <div>
                          <span className="text-lg">{item.text}</span>
                          <span className="text-xs   font-medium mb-4">
                            {renderRichText(item.value)}
                          </span>
                        </div>

                      </Flex>

                    </li>
                  ))}
 
                </ul>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default ProductDetailsPage;
